import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { comprasApi } from '../api/compras';
import { toast } from 'sonner';

export function useCompras(params?: any) {
  return useQuery({
    queryKey: ['compras', params],
    queryFn: () => comprasApi.getAll(params),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 15,
    refetchIntervalInBackground: true,
  });
}

export function useCompra(id: number) {
  return useQuery({
    queryKey: ['compras', id],
    queryFn: () => comprasApi.getById(id),
    enabled: !!id && id > 0,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCompraStats() {
  return useQuery({
    queryKey: ['compras', 'stats'],
    queryFn: () => comprasApi.getStats(),
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 30,
  });
}

export function useCreateCompra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => comprasApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compras'] });
      queryClient.invalidateQueries({ queryKey: ['compras', 'stats'] });
      toast.success('Compra creada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear la compra');
    },
  });
}

export function useUpdateCompra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      comprasApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['compras'] });
      queryClient.invalidateQueries({ queryKey: ['compras', id] });
      queryClient.invalidateQueries({ queryKey: ['compras', 'stats'] });
      toast.success('Compra actualizada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar la compra');
    },
  });
}

export function useDeleteCompra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => comprasApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compras'] });
      queryClient.invalidateQueries({ queryKey: ['compras', 'stats'] });
      toast.success('Compra eliminada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar la compra');
    },
  });
}

export function useGenerateCompraPDF() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => comprasApi.generatePDF(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['compras'] });
      queryClient.invalidateQueries({ queryKey: ['compras', id] });
    },
    onError: (error: any) => {
      console.error('Error en generatePDF:', error);
    },
  });
}